import { api } from '@/lib/api'
import { newIdempotencyKey } from '@/lib/idempotency'
import { z } from 'zod'

export const vitalSchema = z.object({
  id: z.string(),
  resident_id: z.string(),
  facility_id: z.string(),
  taken_at: z.string(),
  recorded_by: z.string(),
  bp_systolic: z.number().int().nullable(),
  bp_diastolic: z.number().int().nullable(),
  heart_rate: z.number().int().nullable(),
  respiratory_rate: z.number().int().nullable(),
  temperature_f: z.number().nullable(),
  spo2: z.number().int().nullable(),
  weight_lb: z.number().nullable(),
  pain_score: z.number().int().nullable(),
  blood_glucose: z.number().int().nullable(),
  notes: z.string().nullable(),
  // Extended vital fields
  height_in: z.number().nullish().default(null),
  bmi: z.number().nullish().default(null),
  respiratory_volume: z.number().nullish().default(null),
  perfusion_index: z.number().nullish().default(null),
  peak_expiratory_flow: z.number().nullish().default(null),
  force_expiratory_volume: z.number().nullish().default(null),
  pulse_rate: z.number().int().nullish().default(null),
})
export type Vital = z.infer<typeof vitalSchema>

export interface CreateVitalInput {
  resident_id: string
  taken_at?: string
  bp_systolic?: number | null
  bp_diastolic?: number | null
  heart_rate?: number | null
  respiratory_rate?: number | null
  temperature_f?: number | null
  spo2?: number | null
  weight_lb?: number | null
  pain_score?: number | null
  blood_glucose?: number | null
  notes?: string | null
  height_in?: number | null
  bmi?: number | null
  respiratory_volume?: number | null
  perfusion_index?: number | null
  peak_expiratory_flow?: number | null
  force_expiratory_volume?: number | null
  pulse_rate?: number | null
}

export async function listVitals(residentId: string): Promise<Vital[]> {
  const raw = await api.get<unknown[]>('/vitals', {
    params: { resident_id: residentId, page_size: 100 },
  })
  return raw.map((v) => vitalSchema.parse(v))
}

export interface FaxJobSummary {
  id: string
  vital_ids: string[]
  status: 'queued' | 'sending' | 'sent' | 'failed' | 'permanently_failed' | 'canceled'
  recipient_name: string | null
  sent_at: string | null
  failed_at: string | null
}

export async function listFaxJobsForResident(residentId: string): Promise<FaxJobSummary[]> {
  return api.get<FaxJobSummary[]>('/fax-jobs', { params: { resident_id: residentId, page_size: 200 } })
}

export async function createVital(input: CreateVitalInput): Promise<Vital> {
  const raw = await api.post<unknown>('/vitals', input as unknown as Record<string, unknown>, {
    idempotencyKey: newIdempotencyKey(),
  })
  return vitalSchema.parse(raw)
}
